import { LitElement, html, css, utils } from '@hh/global.js'
const { debounce } = utils

class HHHero extends LitElement {

  static properties = {
    type: {
      type: String,
    }
  }

  constructor() {
    super()
    this.type = 'hero-neon-glow'
  }

  connectedCallback() {
    super.connectedCallback()

    switch (this.type) {
      case 'hero-x-overlay':
        this.initXOverlay()
        break
      case 'hero-simple':
        this.initHeroControls()
        break
      case 'hero-neon-glow':
        this.initNeonGlow()
        break
    }
  }

  initNeonGlow() {
    this.hero = this.querySelector('.hero__media') ?? null

    if (this.hero) {
      const maxStyle = this.hero.currentStyle || window.getComputedStyle(this.hero)
      this.maxPaddingTopBottom = parseInt(maxStyle.paddingTop)
      this.maxPaddingLeftRight = parseInt(maxStyle.paddingLeft)
      this.minPadding = this.newPaddingTopBottom = this.newPaddingLeftRight = this.childNewPaddingTopBottom = this.childNewPaddingLeftRight = 0

      this.heroChild = this.hero.querySelector('.hero__media-wrapper')
      const childMaxStyle = this.heroChild.currentStyle || window.getComputedStyle(this.heroChild)
      this.childMaxPaddingTopBottom = parseInt(childMaxStyle.paddingTop)
      this.childMaxPaddingLeftRight = parseInt(childMaxStyle.paddingLeft)

      this.heroOffsetTop = this.offsetTop

      window.addEventListener('scroll', () => { window.requestAnimationFrame(this.scrollAnimation.bind(this)) }, false)

      this.initHeroControls()
    }
  }

  initXOverlay() {
    this.hero = window.matchMedia('(min-width: 990px)').matches ? this.querySelector('.hero__media .hero__media--desktop') : this.querySelector('.hero__media .hero__media--mobile') ?? null
    this.xOverlayScroll = false
    if (this.hero) {
      window.addEventListener('scroll', this.zoomAnimation.bind(this))
      this.initHeroControls()
      this.debouncedMandatoryScroll = debounce((event) => {
        this.mandatoryScroll(event)
      }, 100)
      this.querySelectorAll('.js-scroll-wrapper').forEach(element => {
        element.addEventListener('scroll', this.debouncedMandatoryScroll.bind(this))
      })
    }
  }

  mandatoryScroll(e) {
    const scrollWrapper = e.target
    const scrollTop = scrollWrapper.scrollTop
    const viewportHeight = (scrollWrapper.innerHeight || scrollWrapper.clientHeight)

    if (scrollTop > 0 && scrollTop < (viewportHeight - (viewportHeight * 0.15))) {
      if (scrollTop <= viewportHeight * 0.25) {
        scrollWrapper.scrollTo({
          top: 0,
          behavior: 'smooth'
        })
      }

      if (scrollTop > viewportHeight * 0.25) {
        scrollWrapper.scrollTo({
          top: viewportHeight,
          behavior: 'smooth'
        })
      }
    }
  }

  zoomAnimation() {
    const heroDimensions = this.hero.getBoundingClientRect();
    const viewportHeight = (window.innerHeight || document.documentElement.clientHeight);
    if (!this.xOverlayScroll && (heroDimensions.top <= viewportHeight * 0.2) && (heroDimensions.bottom >= viewportHeight * 0.2)) {
      this.xOverlayScroll = true
      this.parentNode.classList.add('in-view')
      setTimeout((function () {
        this.parentNode.classList.add('x-overlay-animate')
      }).bind(this), 600);
    }
  }

  initHeroControls() {
    const videoEl = (this.type != 'hero-x-overlay') ? this.querySelectorAll('adaptive-video') : this.querySelectorAll('adaptive-video.hero__mask--bg')
    const isVideoPlaying = video => !!(video.currentTime > 0 && !video.paused && !video.ended && video.readyState > 2);

    this.controls?.forEach((control, controlIndex) => {
      if(isVideoPlaying(videoEl[controlIndex])) {
        control.setAttribute('aria-label', 'Play')
      } else {
        control.setAttribute('aria-label', 'Pause')
      }
      control.addEventListener('click', () => {
        const video = videoEl[controlIndex]?.shadowRoot?.querySelector('video')
        if (video) {
          if (isVideoPlaying(video)) {
            video.pause()
            control.classList.add('--is-paused')
            control.setAttribute('aria-label', 'Play')
          } else {
            video.play()
            control.classList.remove('--is-paused')
            control.setAttribute('aria-label', 'Pause')
          }
        }
      })
    })

    this.muteControls?.forEach((control, controlIndex) => {
      control.addEventListener('click', () => {
        const video = videoEl[controlIndex]?.shadowRoot?.querySelector('video')
        if (!video) {
          return
        }

        video.muted = !video.muted
        control.classList[video.muted ? 'remove' : 'add']('--is-muted')
      })
    })
  }

  scrollAnimation() {
    let scrollTop = window.scrollY

    if (scrollTop < (this.heroOffsetTop + this.offsetHeight)) {
      this.childNewPaddingTopBottom = this.childMaxPaddingTopBottom - (scrollTop * (this.childMaxPaddingTopBottom - this.minPadding) / (this.offsetTop + this.offsetHeight)) - 4
      this.childNewPaddingLeftRight = this.childMaxPaddingLeftRight - (scrollTop * (this.childMaxPaddingLeftRight - this.minPadding) / (this.offsetTop + this.offsetHeight)) - 4
      this.heroChild.style.padding = `${Math.min(this.childNewPaddingTopBottom)}px ${Math.min(this.childNewPaddingLeftRight)}px`

      if (scrollTop > (this.heroOffsetTop)) {
        this.hero.style.padding = `${this.maxPaddingTopBottom}px ${this.minPadding}px`
        this.heroChild.style.padding = `${this.minPadding}px`
        this.parentNode.classList.add('in-view')
      } else {
        this.hero.style.padding = `${this.maxPaddingTopBottom}px ${this.maxPaddingLeftRight}px`
        this.heroChild.style.padding = `${this.childMaxPaddingTopBottom}px ${this.childMaxPaddingLeftRight}px`
        this.parentNode.classList.remove('in-view')
      }
    }

    if (scrollTop > ((this.heroOffsetTop + this.offsetHeight) - this.totalHeaderHeight)) {
      this.hero.style.padding = `${this.maxPaddingTopBottom}px ${this.maxPaddingLeftRight}px`
      this.heroChild.style.padding = `${this.childMaxPaddingTopBottom}px ${this.childMaxPaddingLeftRight}px`
      this.parentNode.classList.remove('in-view')
    }
  }

  get totalHeaderHeight() {
    return (this.header?.offsetHeight || 0) + (this.announcementBar?.offsetHeight || 0)
  }

  get header() {
    return document.querySelector('.js-header') ?? null
  }

  get announcementBar() {
    return document.querySelector('.js-announcement-bar') ?? null
  }

  get controls() {
    return this.querySelectorAll('.js-hero-controls') ?? null
  }

  get muteControls() {
    return this.querySelectorAll('.js-hero-mute-controls') ?? null
  }

  render() {
    return html`<slot></slot>`
  }
}

customElements.define('hh-hero', HHHero)
